import { useAuth, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { EditTeamChannelDialog, PageHeader, TeamChatConversation, TeamChatHeader } from "../../components";
import { Button, DestructiveConfirmDialog } from "@tellescope/ui";
import { Spinner } from "@tellescope/design-system";
import { DoorOpen, Trash2 } from "lucide-react";

export const TeamChatPage = () => {
  const { t } = useTranslation("team-chat");
  const { currentUser } = useAuth();
  const params = useParams();
  const [search] = useSearchParams();
  const chatroomId = params.chatroomId;
  const navigate = useNavigate();

  const chatRoom = useTellescopeQuery({
    model: "chat_rooms",
    method: "getOne",
    request: chatroomId ?? "",
    enabled: !!chatroomId,
  });

  const deleteChatroomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "deleteOne",

    onMutate: () => navigate("/", { replace: true }),
  });

  const updateChatroomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "updateOne",
  });

  if (!chatroomId) {
    return <Navigate to="/404" />;
  }

  if (!chatRoom.data || chatRoom.isLoading) {
    return (
      <div className="w-full min-h-svh flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="h-svh flex flex-col overflow-y-hidden">
      <PageHeader>
        <div className="flex w-full justify-between items-center">
          <EditTeamChannelDialog chatroomId={chatroomId}>
            <Button variant="link" className="text-lg gap-1 pl-0 flex items-center">
              <span>{chatRoom.data?.emoji ?? "❓"}</span>
              {chatRoom.data?.title ?? t("Untitled Chat")}
            </Button>
          </EditTeamChannelDialog>
          <div className="flex gap-2 items-center">
            <DestructiveConfirmDialog
              title={t('Delete "{{channelName}}" Channel?', { channelName: chatRoom.data?.title })}
              description={t("This will permanently delete the channel. This action cannot be undone.")}
              onConfirm={() => deleteChatroomMutation.mutateAsync(chatroomId)}
            >
              <Button variant="ghost" size="sm-icon" hoverLabel={t("Delete Channel")}>
                <Trash2 />
              </Button>
            </DestructiveConfirmDialog>
            <Button
              variant="ghost"
              size="sm-icon"
              hoverLabel={t("Leave Channel")}
              onClick={() => {
                updateChatroomMutation.mutateAsync(
                  {
                    id: chatroomId,
                    userIds: chatRoom.data?.userIds?.filter((id) => id !== currentUser.id) ?? [],
                    _options: {
                      replaceObjectFields: true,
                    },
                  },
                  { onSuccess: () => navigate("/", { replace: true }) }
                );
              }}
            >
              <DoorOpen />
            </Button>
          </div>
        </div>
      </PageHeader>
      <TeamChatHeader chatroomId={chatroomId} />
      <TeamChatConversation chatroomId={chatroomId} draftId={search.get("draftId") ?? undefined} />
    </div>
  );
};
